<template>
  <div>
    <div class="flex align-middle">
      <p class="label">{{ $t('transfer_comparison.you_pay') }}</p>
      <div class="flex-1 self-start leading-none border-b border-dashed border-gray-300 mx-1">
        &nbsp;
      </div>
      <strong class="text-right">{{ corridorAmount }}</strong>
    </div>
    <div class="flex mt-3">
      <p>{{ $t('transfer_comparison.fee') }}</p>
      <div class="flex-1 self-start leading-none border-b border-dashed border-gray-300 mx-1">
        &nbsp;
      </div>
      <div class="text-right font-semibold">
        <p>{{ fee }}</p>
        <p
          v-if="service.promotion && service.promotion.fee"
          class="text-12 lg:text-14 text-blue-900 line-through"
        >
          {{ standardFee }}
        </p>
      </div>
    </div>
    <button
      v-if="extFees"
      class="w-full hover-underline cursor-pointer flex items-baseline mt-3"
      @click="modalOpen = true"
    >
      <span class="dotted-underline">{{ $t('external_fees.label') }}</span>
      <svg role="presentation" focusable="false" tabindex="-1" class="icon icon-xs align-baseline ml-1.5"
         
        alt=""
        svg-inline
       xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none">
<path fill="currentColor" fill-rule="evenodd" d="M6 11.25A5.25 5.25 0 1 0 6 .75a5.25 5.25 0 0 0 0 10.5ZM6 12A6 6 0 1 0 6 0a6 6 0 0 0 0 12Z" clip-rule="evenodd"/>
<path fill="currentColor" d="M5.14 6a.86.86 0 0 1 1.72 0v2.57a.86.86 0 0 1-1.72 0V6ZM5.14 3.43a.86.86 0 1 1 1.72 0 .86.86 0 0 1-1.72 0Z"/>
</svg>
      <span class="flex-1 self-start leading-none border-b border-dashed border-gray-300 mx-1">
        &nbsp;
      </span>
      <span class="text-right font-semibold">
        <span>{{ extFees }}</span>
        <span class="block text-12 lg:text-14 text-amber-800">
          {{ $t('label.estimated') }}
        </span>
      </span>
      <ExternalFeesModal
        v-if="modalOpen"
        :provider="provider"
        :quote="service.quote"
        @close="modalOpen = false"
      />
    </button>
    <div class="flex mt-3">
      <p>{{ $t('exchange_rate') }}</p>
      <div class="flex-1 self-start leading-none border-b border-dashed border-gray-300 mx-1">
        &nbsp;
      </div>
      <div class="text-right font-semibold">
        <p>{{ formatRate(exchangeRate, $lang) }}</p>
        <p
          v-if="service.promotion && service.promotion.rate"
          class="text-12 lg:text-14 text-blue-900 line-through"
        >
          {{ formatRate(service.quote.rate, $lang) }}
        </p>
      </div>
    </div>
    <ExchangeRate
      :service="service"
      :mid-market-rate="midMarketRate"
      show-mid-market
      v-slot="slotProps"
    >
      <p class="text-12 lg:text-14 font-semibold mt-1">
        1 {{ corridor.currencyFrom.iso }} = {{ slotProps.exchangeRate }}
        {{ corridor.currencyTo.iso }}
      </p>
    </ExchangeRate>

    <div class="flex justify-between items-baseline mt-3 pt-3 border-t-2 border-gray-200">
      <strong class="text-12 lg:text-16">{{ $t('transfer_comparison.recipient_gets') }}</strong>
      <div class="text-right">
        <p class="font-bold text-18 lg:text-24">{{ amount }}</p>
        <p
          v-if="service.promotion && amount !== standardAmount"
          class="font-semibold text-12 lg:text-14 text-blue-900 line-through"
        >
          {{ standardAmount }}
        </p>
      </div>
    </div>
    <p v-if="extFees" class="text-right font-semibold text-12 lg:text-14 text-amber-800">
      {{ $t('label.estimated') }}
    </p>
    <p class="text-right text-12 lg:text-14 mt-2">
      {{ $t(`payout.transferred_to_${service.quote.payout}`) }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import type { Corridor } from '@core/types/imt'
import type { Service, Provider } from './types'
import ExternalFeesModal from './external-fees-modal.vue'
import ExchangeRate from './exchange-rate.vue'
import { externalFees, promotionalQuoteFees, standardQuoteFees } from './helpers/pricing'

const props = defineProps<{
  provider: Provider
  service: Service
  midMarketRate: number
}>()

const { $lang } = useNuxtApp()

const corridor = inject('corridor') as Corridor

const modalOpen = ref(false)

const corridorAmount = computed(() => {
  return formatMoney(corridor.amount || corridor.defaultAmount, corridor.currencyFrom.iso, $lang, 2)
})

const amount = computed(() => {
  return formatMoney(props.service.amount, corridor.currencyTo.iso, $lang)
})

const standardAmount = computed(() => {
  return formatMoney(props.service.quote.receivedAmount, corridor.currencyTo.iso, $lang)
})

const exchangeRate = computed(() => {
  return props.service.promotion?.rate ?? props.service.quote.rate
})

const fee = computed(() => {
  const promoFee = promotionalQuoteFees(props.service)
  return promoFee === null
    ? standardFee.value
    : formatMoney(promoFee, corridor.currencyFrom.iso, $lang)
})

const standardFee = computed(() => {
  return formatMoney(standardQuoteFees(props.service), corridor.currencyFrom.iso, $lang, 2)
})

const extFees = computed(() => {
  const extFees = externalFees(props.service)
  return extFees ? formatMoney(extFees, corridor.currencyFrom.iso, $lang) : extFees
})
</script>

<style scoped>
.hover-underline:hover .dotted-underline {
  text-decoration-style: solid;
}
</style>
